import { sharePointDriveService } from '@/services/sharepoint-drive/sharepoint-drive-service';
import { directoryKeys } from '@/utils/queryKeys';
import { useMutation, useQueryClient } from '@tanstack/vue-query';
import type { FileManagerMountsState } from '../logic/core/useFileManagerMounts';
import type { FileSystemItem } from '../types';

// No per-file onError toast here (unlike this mutation's original shape): callers driving batches
// of files (useFileManagerUpload.ts, useNativeFileDropTarget.ts) now surface one batched summary
// toast at the end instead, to avoid toast-spam on large folder trees.
//
// Takes the mounts state explicitly rather than self-injecting via useFileManagerMounts(): this
// mutation is used from provideFileManagerUpload(), called directly in FileManager.vue's own
// setup() — the same instance that calls provideFileManagerMounts(), which a component can
// never inject its own provide() call from.
export const useUploadFileMutation = (
  mounts: Pick<FileManagerMountsState, 'resolve'>,
) => {
  const queryClient = useQueryClient();
  const { resolve } = mounts;

  return useMutation({
    mutationKey: ['directoryService', 'uploadFile'],
    mutationFn: ({
      parentPath,
      file,
    }: {
      parentPath: string;
      file: File;
    }): Promise<FileSystemItem> => {
      // parentPath is virtual; resolved here, at the single point that needs a driveId. Callers are
      // gated on canUploadTo(), so an unresolvable path is a bug rather than a user action.
      const target = resolve(parentPath);
      if (!target) {
        throw new Error(`Cannot upload to ${parentPath}: no accessible library`);
      }
      return sharePointDriveService.uploadFile(target, file);
    },
    // Invalidation stays in virtual paths — the whole file manager caches by them.
    onSuccess: (_data, { parentPath }) =>
      queryClient.invalidateQueries({
        queryKey: directoryKeys.children(parentPath),
      }),
  });
};
